import { unformat } from 'accounting';
import { action, computed, observable, toJS } from 'mobx';
import { app } from '../App';

interface IRepSalesDataFromApi {
  SlpCode: number;
  SlpName: string;
  Today: string | null;
  ThisWeek: string | null;
  ThisMonth: string | null;
  ReceiptAmt: string | null;
}

export interface IRepSales {
  salesToday: number;
  salesThisWeek: number;
  salesThisMonth: number;
  collectionsThisMonth: number;
}

interface IMonthlyCollectionFromApi {
  year: number;
  month: number;
  total: number;
}

export interface IMonthlyCollection {
  key: string;
  year: number;
  month: number;
  amount: number;
}

export interface ICollectionPayment {
  docnum: number;
  docentry: number;
  docdate: string;
  cardcode: string;
  cardname: string;
  doctotal: number;
  slpcode: number;
}

export interface IMonthlyCollectionDetails {
  key: string;
  fetchStatus: CollectionsFetchStatus;
  fetchLastTimestamp: number;
  payments: ICollectionPayment[];
}

export enum CollectionsFetchStatus {
  None,
  Loading,
  Error,
}

export class OrderPadSales {
  private MS_TO_INVALIDATE = 1000 * 60 * 30;

  @observable public isSalesDataLoading: boolean = false;
  @observable public lastSalesDataFetchTimestamp: number = 0;
  @observable public salesData: IRepSales | null = null;

  @observable public showCollectionsModal: boolean = false;

  @observable public collectionsMonthsFetchStatus: CollectionsFetchStatus =
    CollectionsFetchStatus.None;
  @observable public collectionsMonthsData: IMonthlyCollection[] = [];
  @observable
  public collectionsDetailedData: IMonthlyCollectionDetails[] = [];

  @computed
  public get collectionsDetailedDataKeys(): string[] {
    return this.collectionsDetailedData.map((data) => data.key);
  }

  @action
  public toggleCollectionsModal = (show?: boolean) => {
    this.showCollectionsModal =
      typeof show === 'boolean' ? show : !this.showCollectionsModal;
  };

  @action
  public fetchSalesData = async (): Promise<boolean> => {
    try {
      if (
        this.salesData !== null &&
        this.lastSalesDataFetchTimestamp < Date.now() - this.MS_TO_INVALIDATE
      ) {
        return true;
      }

      this.isSalesDataLoading = true;

      const service = app.service('rep-sales');
      const repSales: IRepSalesDataFromApi = await service.find();

      this.salesData = {
        salesToday: repSales.Today ? unformat(repSales.Today) : 0,
        salesThisWeek: repSales.ThisWeek ? unformat(repSales.ThisWeek) : 0,
        salesThisMonth: repSales.ThisMonth ? unformat(repSales.ThisMonth) : 0,
        collectionsThisMonth: repSales.ReceiptAmt
          ? unformat(repSales.ReceiptAmt)
          : 0,
      };

      this.lastSalesDataFetchTimestamp = Date.now();
      this.isSalesDataLoading = false;

      return true;
    } catch (error) {
      setTimeout(() => {
        this.fetchSalesData();
      }, 1000);

      throw error;
    }
  };

  @action
  public fetchMonthlyCollections = async (): Promise<boolean> => {
    try {
      this.collectionsMonthsFetchStatus = CollectionsFetchStatus.Loading;

      const repCollections = [] as IMonthlyCollectionFromApi[];

      this.collectionsMonthsData = repCollections.map((collection) => ({
        key: collection.year + '-' + collection.month,
        year: collection.year,
        month: collection.month,
        amount: collection.total,
      }));

      this.collectionsMonthsFetchStatus = CollectionsFetchStatus.None;

      return true;
    } catch (error) {
      if (this.collectionsMonthsData.length > 0) {
        this.collectionsMonthsFetchStatus = CollectionsFetchStatus.None;
        return true;
      }

      this.collectionsMonthsFetchStatus = CollectionsFetchStatus.Error;

      throw error;
    }
  };

  @action
  public fetchDetailedCollectionsForMonth = async (
    key: string
  ): Promise<boolean> => {
    try {
      let index = -1;
      this.collectionsDetailedDataKeys.some((dataKey, dataIndex) => {
        if (dataKey === key) {
          index = dataIndex;
          return true;
        }

        return false;
      });

      if (index === -1) {
        this.collectionsDetailedData.push({
          key,
          fetchLastTimestamp: 0,
          fetchStatus: CollectionsFetchStatus.None,
          payments: [],
        });

        index = this.collectionsDetailedData.length - 1;
      }

      this.collectionsDetailedData[index].fetchStatus =
        CollectionsFetchStatus.Loading;

      const payments = [] as ICollectionPayment[];

      this.collectionsDetailedData[index].fetchStatus =
        CollectionsFetchStatus.None;
      this.collectionsDetailedData[index].fetchLastTimestamp = Date.now();
      this.collectionsDetailedData[index].payments = payments;

      return true;
    } catch (error) {
      let index = -1;
      this.collectionsDetailedDataKeys.some((dataKey, dataIndex) => {
        if (dataKey === key) {
          index = dataIndex;
          return true;
        }

        return false;
      });

      if (index !== -1) {
        if (this.collectionsDetailedData[index].payments.length > 0) {
          this.collectionsDetailedData[index].fetchStatus =
            CollectionsFetchStatus.None;
          return true;
        }

        this.collectionsDetailedData[index].fetchStatus =
          CollectionsFetchStatus.Error;
      }

      throw error;
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/OrderPadSales.ts